import { createAssetSrc, isMobileDevice } from '@frontend/utils';

type MethodType = 'wallet' | 'exchange';
type Platform = 'only-web' | 'only-mobile' | 'cross';

interface Method {
  apiId: MethodApiId;
  name?: string;
  asset: string;
  type: MethodType;
  description?: string;
  platform?: Platform;
  disabled?: boolean;
}

interface ApiMethod {
  name: string;
  id: MethodApiId;
}

type ExtendedApiMethod = ApiMethod & Omit<Method, 'apiId'>;

enum MethodApiId {
  Metamask = 'metamask',
  xPortal = 'xportal',
  MvxExtensionWallet = 'multiverse-defi-wallet',
  WalletConnect = 'wallet-connect',
  WalletAnother = 'wallet-another',
  ExchangeAnother = 'exchange-another',
  BinancePay = 'binance_pay',
  SolanaPay = 'solana_pay',
}

const wallets: Method[] = [
  {
    apiId: MethodApiId.WalletConnect,
    asset: createAssetSrc('walletconnect', 'wallets'),
    type: 'wallet',
    description: 'Trust Wallet, OKX, Blockchain.com, etc',
  },
  {
    apiId: MethodApiId.Metamask,
    asset: createAssetSrc('metamask', 'wallets'),
    type: 'wallet',
  },
  {
    apiId: MethodApiId.xPortal,
    asset: createAssetSrc('xportal', 'wallets'),
    type: 'wallet',
    disabled: false,
  },
  {
    apiId: MethodApiId.MvxExtensionWallet,
    asset: createAssetSrc('mvx-extension', 'wallets'),
    type: 'wallet',
    platform: 'only-web',
    disabled: false,
  },
  {
    apiId: MethodApiId.WalletAnother,
    asset: '',
    type: 'wallet',
  },
];

const otherMethods: Method[] = [
  {
    apiId: MethodApiId.ExchangeAnother,
    name: 'Exchange',
    type: 'exchange',
    asset: '',
  },
];

const methods = [...otherMethods, ...wallets];

const isExchange = (method: MethodApiId) =>
  method === MethodApiId.ExchangeAnother;

const mergeWithApiMethod = (method: Method, apiMethod: ApiMethod) => {
  return {
    id: method.apiId,
    name: method.name || apiMethod?.name || '',
    description: method.description,
    asset: method.asset,
    type: method.type,
    platform: method.platform,
    disabled: method.disabled,
  };
};

const extendApiMethods = (apiMethods: ApiMethod[]) => {
  return methods
    .map((method) => {
      const foundMethod = apiMethods.find(
        (apiMethod) => apiMethod.id === method.apiId
      );

      if (!foundMethod) {
        return null;
      }

      return mergeWithApiMethod(method, foundMethod);
    })
    .filter((method) => method !== null) as ExtendedApiMethod[];
};

const extendApiMethod = (apiMethod: ApiMethod) => {
  const method = findMethodById(apiMethod.id);

  if (!method) {
    return null;
  }

  return mergeWithApiMethod(method, apiMethod);
};

const findMethodById = (apiId: MethodApiId) => {
  return methods.find((method) => method.apiId === apiId);
};

const getMethods = (apiMethods: ApiMethod[], type?: MethodType) => {
  const extendedApiMethods = extendApiMethods(apiMethods);

  const filteredByPlatform = extendedApiMethods.filter((method) => {
    if (isMobileDevice() && method?.platform === 'only-web') {
      return false;
    }

    return true;
  });

  if (!type) {
    return filteredByPlatform;
  }

  return filteredByPlatform.filter((apiMethod) => apiMethod.type === type);
};

const isMethodAnother = (methodApiId: MethodApiId) => {
  return [MethodApiId.ExchangeAnother, MethodApiId.WalletAnother].includes(
    methodApiId
  );
};

export {
  methods,
  MethodApiId,
  MethodType,
  Method,
  ExtendedApiMethod,
  ApiMethod,
  extendApiMethods,
  extendApiMethod,
  isExchange,
  isMethodAnother,
  getMethods,
  findMethodById,
};
